import React, { useState, useEffect } from "react";
import {
    Box,
    Paper,
    Typography,
    Stepper,
    Step,
    StepLabel,
    Button,
    TextField,
    Autocomplete,
    Grid,
    Card,
    CardContent,
    CardActionArea,
    Divider,
    Alert,
    CircularProgress,
    MenuItem
} from "@mui/material";
import {
    PersonSearch,
    HealthAndSafety,
    Assignment,
    CheckCircle,
    AttachMoney
} from "@mui/icons-material";
import api from '../../services/api.js';
import { formatarMoeda, formatarCPF } from '../../utils/formatadores.js';
import { useNavigate } from "react-router-dom";

const steps = ['Selecionar Beneficiário', 'Escolher Plano', 'Revisão e Contrato'];

export default function NovoContrato() {
    const navigate = useNavigate();
    const [activeStep, setActiveStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Data States
    const [beneficiarios, setBeneficiarios] = useState([]);
    const [planos, setPlanos] = useState([]);
    const [representantes, setRepresentantes] = useState([]); // If needed for admin to select rep

    // Selection States
    const [selectedBeneficiario, setSelectedBeneficiario] = useState(null);
    const [selectedPlano, setSelectedPlano] = useState(null);
    const [diaVencimento, setDiaVencimento] = useState(10);
    const [idRepresentante, setIdRepresentante] = useState(""); // Optional if user is admin

    useEffect(() => {
        // Load initial data based on step? Or pre-load?
        // Let's pre-load beneficiaries (searchable) and plans.
        carregarPlanos();
        // Beneficiaries will be loaded via search for performance, but for now lets load all (MVP)
        carregarBeneficiarios();
        // If user is admin, load reps
        carregarRepresentantes();
    }, []);

    const carregarPlanos = async () => {
        try {
            const res = await api.get('/plano?ativo=true');
            setPlanos(res.data.dados || []);
        } catch (err) {
            console.error("Erro ao carregar planos", err);
        }
    };

    const carregarBeneficiarios = async () => {
        // TODO: Implement server-side search for beneficiaries to avoid loading thousands
        // For now, fetching list (assuming < 1000 active without contracts maybe?)
        try {
            const res = await api.get('/beneficiario');
            setBeneficiarios(res.data.dados || []);
        } catch (err) {
            console.error("Erro ao carregar beneficiarios", err);
        }
    };

    const carregarRepresentantes = async () => {
        try {
            const res = await api.get('/representante'); // Need an endpoint for list
            // If endpoint doesn't exist yet, we might skip or fail gracefully
            if (res.data && res.data.dados) setRepresentantes(res.data.dados);
        } catch (err) {
            // endpoint might not exist specifically for listing simple selector
        }
    };

    const handleNext = async () => {
        if (activeStep === 0 && !selectedBeneficiario) {
            setError("Selecione um beneficiário titular.");
            return;
        }
        if (activeStep === 1 && !selectedPlano) {
            setError("Selecione um plano de saúde.");
            return;
        }
        if (activeStep === 2) {
            // Submit
            await criarContrato();
            return;
        }

        setError(null);
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const criarContrato = async () => {
        setLoading(true);
        try {
            const payload = {
                id_beneficiario_titular: selectedBeneficiario.id_beneficiario,
                id_plano: selectedPlano.id_plano,
                nu_dia_vencimento: diaVencimento,
                id_representante: idRepresentante || undefined // If null backend uses logged user if rep
            };

            await api.post('/contrato', payload);
            // On success, redirect to contract list or show success step
            alert("Contrato criado com sucesso!");
            navigate('/contratos');
        } catch (err) {
            console.error(err);
            setError("Erro ao criar contrato. Verifique os dados.");
            setLoading(false);
        }
    };

    // Components for Steps
    const renderStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="h6" gutterBottom>Quem será o titular do contrato?</Typography>
                        <Autocomplete
                            options={beneficiarios}
                            getOptionLabel={(option) => `${option.usuario?.st_nome} (CPF: ${formatarCPF(option.st_cpf)})`}
                            value={selectedBeneficiario}
                            onChange={(event, newValue) => setSelectedBeneficiario(newValue)}
                            renderInput={(params) => <TextField {...params} label="Buscar Beneficiário" variant="outlined" fullWidth />}
                            noOptionsText="Nenhum beneficiário encontrado"
                        />
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Apenas beneficiários titulares podem contratar. Caso não encontre, cadastre-o primeiro em "Beneficiários".
                            </Typography>
                        </Box>

                        {/* Optional Representante Selection for Admins */}
                        {representantes.length > 0 && (
                            <Box sx={{ mt: 3 }}>
                                <Typography variant="subtitle2">Vendedor / Representante (Opcional)</Typography>
                                <TextField
                                    select
                                    fullWidth
                                    label="Selecione o Representante"
                                    value={idRepresentante}
                                    onChange={(e) => setIdRepresentante(e.target.value)}
                                >
                                    <MenuItem value=""><em>Nenhum (Venda Direta)</em></MenuItem>
                                    {representantes.map((rep) => (
                                        <MenuItem key={rep.id_representante} value={rep.id_representante}>
                                            {rep.st_nome}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Box>
                        )}
                    </Box>
                );
            case 1:
                return (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="h6" gutterBottom>Selecione o Plano ideal</Typography>
                        <Grid container spacing={2}>
                            {planos.map((plano) => (
                                <Grid item xs={12} sm={6} md={4} key={plano.id_plano}>
                                    <Card
                                        sx={{
                                            border: selectedPlano?.id_plano === plano.id_plano ? '2px solid #20ac9c' : '1px solid #ddd',
                                            backgroundColor: selectedPlano?.id_plano === plano.id_plano ? 'rgba(32, 172, 156, 0.05)' : 'background.paper'
                                        }}
                                    >
                                        <CardActionArea onClick={() => setSelectedPlano(plano)} sx={{ p: 2 }}>
                                            <Typography variant="h6" color="primary" gutterBottom>{plano.st_nome}</Typography>
                                            <Divider sx={{ mb: 1 }} />
                                            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                {formatarMoeda(plano.nu_valor_mensal_centavos)}
                                                <Typography component="span" variant="caption">/mês</Typography>
                                            </Typography>
                                            {plano.nu_valor_adesao_centavos > 0 && (
                                                <Typography variant="body2" color="warning.main">
                                                    + {formatarMoeda(plano.nu_valor_adesao_centavos)} (Adesão)
                                                </Typography>
                                            )}
                                            <Box sx={{ mt: 2 }}>
                                                <Typography variant="body2">
                                                    • Até {plano.nu_limite_dependentes} dependentes
                                                </Typography>
                                                <Typography variant="body2">
                                                    • Carência: {plano.nu_carencia_dias_padrao} dias
                                                </Typography>
                                            </Box>
                                        </CardActionArea>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                );
            case 2:
                // Review
                const valorTotalMensal = selectedPlano?.nu_valor_mensal_centavos || 0;
                const valorAdesao = selectedPlano?.nu_valor_adesao_centavos || 0;
                return (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="h6" gutterBottom>Resumo do Contrato</Typography>
                        <Paper sx={{ p: 3 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" color="text.secondary">Titular</Typography>
                                    <Typography variant="body1" gutterBottom>{selectedBeneficiario?.usuario?.st_nome}</Typography>

                                    <Typography variant="subtitle2" color="text.secondary">CPF</Typography>
                                    <Typography variant="body1" gutterBottom>{formatarCPF(selectedBeneficiario?.st_cpf)}</Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" color="text.secondary">Plano Escolhido</Typography>
                                    <Typography variant="body1" gutterBottom>{selectedPlano?.st_nome}</Typography>

                                    <Typography variant="subtitle2" color="text.secondary">Mensalidade</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{formatarMoeda(valorTotalMensal)}</Typography>

                                    {valorAdesao > 0 && (
                                        <>
                                            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>Taxa de Adesão (Pagamento Único)</Typography>
                                            <Typography variant="body1" color="warning.main">{formatarMoeda(valorAdesao)}</Typography>
                                        </>
                                    )}
                                </Grid>
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 2 }} />
                                    <Typography variant="subtitle2" gutterBottom>Dia de Vencimento da Fatura</Typography>
                                    <TextField
                                        select
                                        size="small"
                                        value={diaVencimento}
                                        onChange={(e) => setDiaVencimento(e.target.value)}
                                        helperText="Selecione o melhor dia para pagamento mensal"
                                    >
                                        {[5, 10, 15, 20, 25].map(d => (
                                            <MenuItem key={d} value={d}>Dia {d}</MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                            </Grid>
                        </Paper>
                        <Alert severity="info" sx={{ mt: 2 }}>
                            Ao confirmar, o contrato será gerado com status <strong>RASCUNHO / AGUARDANDO PAGAMENTO</strong>.
                            O link de pagamento da Taxa de Adesão (se houver) ou da primeira mensaldiade será gerado.
                        </Alert>
                    </Box>
                );
            default:
                return 'Unknown step';
        }
    };

    return (
        <Box sx={{ p: 3, maxWidth: 1000, margin: '0 auto' }}>
            <Typography variant="h4" sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Assignment fontSize="large" color="primary" />
                Novo Contrato
            </Typography>

            {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>
            )}

            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                {steps.map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            <Box>
                {renderStepContent(activeStep)}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }}>
                    <Button
                        disabled={activeStep === 0 || loading}
                        onClick={handleBack}
                    >
                        Voltar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleNext}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : (activeStep === steps.length - 1 ? <CheckCircle /> : null)}
                    >
                        {activeStep === steps.length - 1 ? 'Finalizar Contrato' : 'Próximo'}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}
